// iter_CONFIGURABLE_PUBLIC_VENUE_LANDING_PAGE — VISUAL ENHANCEMENT (2026-02-28)
// Public venue landing page. Alternating white / accent sections,
// full-width hero, coloured footer. Purely presentational — no route,
// no API, no business logic changed.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicVenueLanding() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/venues/${venueId}/landing-page`);
        if (!alive) return;
        if (r.data?.enabled === false) {
          setState("disabled"); setData(r.data); return;
        }
        setState("ready"); setData(r.data);
      } catch (e) {
        if (!alive) return;
        const s = e?.response?.status;
        setState(s === 404 ? "not_found" : "error");
        setErrorMsg(e?.response?.data?.detail || "Unable to load venue.");
      }
    })();
    return () => { alive = false; };
  }, [venueId]);

  if (state === "loading") {
    return <div className="p-8 text-center text-stone-500" data-testid="vlp-loading">Loading…</div>;
  }
  if (state === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="vlp-not-found">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold">Venue not found</h1>
          <p className="mt-2 text-stone-600">This page does not exist or the link is incorrect.</p>
        </div>
      </div>
    );
  }
  if (state === "error") {
    return <div className="p-8 text-center text-red-600" data-testid="vlp-error">{errorMsg}</div>;
  }
  if (state === "disabled") {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="vlp-disabled">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold">This venue&apos;s page is not available</h1>
          <p className="mt-2 text-stone-600">The venue has not published a public landing page yet.</p>
        </div>
      </div>
    );
  }
  return <LandingView v={data.venue} onCard={(url) => onCardNavigate(url, navigate)} />;
}


function onCardNavigate(url, navigate) {
  if (!url) return;
  if (url.startsWith("/")) {
    navigate(url);
  } else {
    window.location.href = url;
  }
}


function LandingView({ v, onCard }) {
  const b = v.branding || {};
  const primary = b.primary_color || "#1c1917";      // stone-900 fallback
  const secondary = b.secondary_color || primary;

  // CSS custom properties so nested components can reference the same
  // colour tokens without prop-drilling. `-tint` is a light background
  // wash we compute via color-mix (graceful fallback below).
  const styleVars = useMemo(() => ({
    "--vlp-primary": primary,
    "--vlp-secondary": secondary,
    "--vlp-primary-tint": `color-mix(in srgb, ${primary} 8%, white)`,
    "--vlp-secondary-tint": `color-mix(in srgb, ${secondary} 8%, white)`,
    "--vlp-primary-hover": `color-mix(in srgb, ${primary} 85%, black)`,
  }), [primary, secondary]);

  const enabledModules = Object.entries(v.modules || {})
    .filter(([, m]) => m?.enabled)
    .map(([k, m]) => ({ key: k, ...m }));

  const hasContactSummary = !!(v.contact?.phone || v.contact?.email
    || v.contact?.website || v.contact?.address_lines?.length);
  const hasHoursOrDelivery = (v.business_hours?.length > 0) || v.delivery?.enabled;
  const hasByoOrBookingNotice = !!(v.byo_message || v.booking_notice);

  return (
    <div
      className="min-h-screen bg-white text-stone-900 overflow-x-hidden"
      style={styleVars}
      data-testid="vlp-page"
    >
      {/* 1. Announcement banner */}
      {v.announcement && (
        <div
          className="w-full text-white text-center py-3 px-4 text-sm sm:text-base font-medium"
          style={{ backgroundColor: "var(--vlp-primary)" }}
          data-testid="vlp-announcement"
          role="status"
        >
          <span aria-hidden className="mr-2">✦</span>
          {v.announcement}
        </div>
      )}

      {/* 2. Hero — image-only for visual impact. */}
      <HeroSection v={v} />

      {/* 3. Navigation cards */}
      <section
        className="w-full py-12 sm:py-16 lg:py-20"
        style={{ backgroundColor: "var(--vlp-primary-tint, #fafaf9)" }}
        data-testid="vlp-cards-section"
      >
        <div className="max-w-6xl mx-auto sm:px-8">
          <CardCarousel
            modules={enabledModules}
            onCard={onCard}
          />
        </div>
      </section>

      {/* 4. Welcome — venue identity now lives here. */}
      <WelcomeSection v={v} />

      {/* 5. Delivery + business hours */}
      {hasHoursOrDelivery && (
        <section
          className="w-full py-16 sm:py-20"
          style={{ backgroundColor: "var(--vlp-secondary-tint, #f5f5f4)" }}
          data-testid="vlp-hours-section"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-8 grid gap-8 sm:grid-cols-2">
            <HoursSummary hours={v.business_hours} />
            <DeliverySummary delivery={v.delivery} />
          </div>
        </section>
      )}

      {/* 6. BYO + booking information */}
      {hasByoOrBookingNotice && (
        <section className="w-full bg-white py-14 sm:py-16" data-testid="vlp-notices-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 space-y-4 text-center">
            {v.byo_message && (
              <div
                className="rounded-2xl border border-stone-200 bg-stone-50 p-5 text-sm text-stone-700"
                data-testid="vlp-byo"
              >
                {v.byo_message}
              </div>
            )}
            {v.booking_notice && (
              <div
                className="rounded-2xl border border-stone-200 bg-stone-50 p-5 text-sm text-stone-700"
                data-testid="vlp-booking-notice"
              >
                {v.booking_notice}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Contact summary (inline strip before footer, only when data
          is available). Kept subtle — the real contact page is linked
          from every module card. */}
      {hasContactSummary && (
        <section
          className="w-full py-10"
          style={{ backgroundColor: "var(--vlp-primary-tint, #fafaf9)" }}
          data-testid="vlp-contact-section"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-8">
            <ContactSummary contact={v.contact} />
            <div className="mt-4 text-center">
              <Link
                to={`/venue/${v.venue_id}/contact`}
                className="inline-block text-sm font-medium underline decoration-2 underline-offset-4"
                style={{ color: "var(--vlp-primary)" }}
                data-testid="vlp-contact-link"
              >
                View full contact details →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* 7. Footer */}
      <Footer v={v} />
    </div>
  );
}


/**
 * Compute a responsive CSS `height` value from the venue's configured
 * hero-height mode. Desktop uses the target height; tablets and phones
 * scale down proportionally via `clamp()` so the image stays attractive
 * without dominating small screens.
 */
function heroHeightForMode(mode, customPx) {
  switch (mode) {
    case "small":
      return "clamp(200px, 30vh, 340px)";
    case "large":
      return "clamp(320px, 60vh, 780px)";
    case "fullscreen":
      // Cap at viewport height on mobile too but keep a safety floor
      // (never smaller than 280px) for very short landscape phones.
      return "clamp(280px, 100vh, 100vh)";
    case "custom": {
      const px = Number.isFinite(customPx) && customPx >= 120 && customPx <= 2000
        ? customPx : 480;
      // Desktop reaches customPx at ~1280px viewport width; phones
      // scale down proportionally with a 200px floor and a customPx
      // ceiling so we never exceed the owner's chosen height.
      const vwPortion = ((px / 1280) * 100).toFixed(2);
      return `clamp(200px, ${vwPortion}vw, ${px}px)`;
    }
    case "medium":
    default:
      return "clamp(240px, 45vh, 540px)";
  }
}


function HeroSection({ v }) {
  const b = v.branding || {};
  const hasHero = !!b.hero_image_url;
  const displayMode = b.hero_display_mode === "fill" ? "fill" : "fit";
  const height = heroHeightForMode(
    b.hero_height_mode || "medium",
    b.hero_height_custom_px
  );

  // FIT mode — full image visible, container adapts to image ratio.
  // The configured hero height acts as a MINIMUM (so the primary
  // colour still shows in the background band when there is no
  // image, and the section still has meaningful visual presence).
  if (displayMode === "fit") {
    return (
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: hasHero ? undefined : height,
          backgroundColor: "var(--vlp-primary)",
        }}
        data-testid="vlp-hero-section"
        data-hero-mode="fit"
      >
        {hasHero && (
          <img
            src={b.hero_image_url}
            alt=""
            className="block w-full h-auto"
            style={{ objectFit: "contain", objectPosition: "center" }}
            data-testid="vlp-hero"
            loading="eager"
          />
        )}
      </section>
    );
  }

  // FILL mode — legacy behaviour. Container has a fixed responsive
  // height and the image covers/crops to fill.
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        height,
        backgroundColor: "var(--vlp-primary)",
      }}
      data-testid="vlp-hero-section"
      data-hero-mode="fill"
    >
      {hasHero && (
        <img
          src={b.hero_image_url}
          alt=""
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "cover", objectPosition: "center" }}
          data-testid="vlp-hero"
          loading="eager"
        />
      )}
      {/* Subtle dark overlay for image consistency only (20-30%). */}
      <div
        className="absolute inset-0"
        style={{
          background: hasHero
            ? "linear-gradient(to bottom, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.30) 100%)"
            : "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)",
        }}
        aria-hidden
      />
    </section>
  );
}


function WelcomeSection({ v }) {
  const b = v.branding || {};
  const hasAny = !!(b.logo_url || v.display_name || v.subtitle
    || v.welcome_message || v.description);
  if (!hasAny) return null;
  return (
    <section
      className="w-full bg-white py-14 sm:py-20"
      data-testid="vlp-welcome-section"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
        {b.logo_url && (
          <img
            src={b.logo_url}
            alt=""
            className="mx-auto h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover bg-white p-1 shadow-md ring-4"
            style={{ borderColor: "transparent" }}
            data-testid="vlp-logo"
          />
        )}
        {v.display_name && (
          <h1
            className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 leading-tight tracking-tight break-words"
            data-testid="vlp-name"
          >
            {v.display_name}
          </h1>
        )}
        {v.subtitle && (
          <p
            className="mt-3 text-lg sm:text-xl text-stone-600 break-words"
            data-testid="vlp-subtitle"
          >
            {v.subtitle}
          </p>
        )}
        {v.welcome_message && (
          <p
            className="mt-8 text-xl sm:text-2xl font-medium leading-snug text-stone-900"
            data-testid="vlp-welcome"
          >
            {v.welcome_message}
          </p>
        )}
        {v.description && (
          <p
            className="mt-6 text-base sm:text-lg text-stone-600 whitespace-pre-line leading-relaxed"
            data-testid="vlp-description"
          >
            {v.description}
          </p>
        )}
      </div>
    </section>
  );
}


function CardCarousel({ modules, onCard }) {
  const scrollerRef = React.useRef(null);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const check = () => {
      // Only relevant when the row scrolls (mobile). If content
      // overflows horizontally AND we're at the start, show hint.
      const overflows = el.scrollWidth - el.clientWidth > 4;
      const atStart = el.scrollLeft < 8;
      setShowHint(overflows && atStart);
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [modules.length]);

  if (modules.length === 0) {
    return (
      <div
        className="mx-4 rounded-2xl bg-white p-8 text-center text-stone-500 shadow-sm"
        data-testid="vlp-no-cards"
      >
        No modules are enabled for this venue yet.
      </div>
    );
  }

  // Below `sm`: horizontal swipe row with scroll-snap.
  // From `sm` upwards: auto-fit grid keeps every card at a readable
  // minimum width and flows into as many rows as needed.

  return (
    <div className="relative">
      {/* Mobile — horizontal snap scroller */}
      <div
        ref={scrollerRef}
        className="sm:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth px-4 pb-4 -mx-px"
        style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
        data-testid="vlp-cards"
      >
        <style>{`
          [data-testid="vlp-cards"]::-webkit-scrollbar { display: none; }
        `}</style>
        {modules.map((m) => (
          <div
            key={m.key}
            className="snap-center shrink-0"
            style={{ width: modules.length === 1 ? "100%" : "85%" }}
          >
            <NavCard
              title={m.title}
              description={m.description}
              buttonText={m.button_text}
              testid={`vlp-card-${m.key}`}
              buttonTestid={`vlp-card-${m.key}-btn`}
              onNavigate={() => onCard(m.url)}
            />
          </div>
        ))}
        {/* trailing spacer so the last card can fully snap-center */}
        <div className="shrink-0" style={{ width: "1px" }} aria-hidden />
      </div>
      {showHint && modules.length > 1 && (
        <div
          className="sm:hidden pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full bg-black/60 text-white shadow-lg animate-pulse"
          data-testid="vlp-swipe-hint"
        >
          Swipe <span aria-hidden>→</span>
        </div>
      )}

      {/* Tablet & desktop — auto-fit grid keeps a minimum readable
          width per card and lets them share one row when space allows.
          At ≥1280px we force N equal columns so up to five enabled
          cards always sit on a single desktop row. */}
      <style>{`
        @media (min-width: 1280px) {
          [data-testid="vlp-cards-grid"] {
            grid-template-columns: repeat(${Math.min(modules.length, 6)}, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
      <div
        className="hidden sm:grid gap-6 lg:gap-8 auto-rows-fr px-4 sm:px-0"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
        data-testid="vlp-cards-grid"
      >
        {modules.map((m) => (
          <NavCard
            key={m.key}
            title={m.title}
            description={m.description}
            buttonText={m.button_text}
            testid={`vlp-card-${m.key}`}
            buttonTestid={`vlp-card-${m.key}-btn`}
            onNavigate={() => onCard(m.url)}
          />
        ))}
      </div>
    </div>
  );
}


function NavCard({ title, description, buttonText, testid, buttonTestid, onNavigate }) {
  return (
    <article
      className="group relative flex h-full flex-col rounded-2xl bg-white p-6 sm:p-7 shadow-sm sm:hover:shadow-xl active:shadow-md transition-all duration-200 border border-stone-200/60 focus-within:ring-2 focus-within:ring-offset-2"
      data-testid={testid}
    >
      <h2 className="text-xl font-semibold text-stone-900 leading-tight break-words">
        {title}
      </h2>
      <p className="mt-3 text-sm text-stone-600 leading-relaxed flex-1 break-words">
        {description}
      </p>
      <button
        type="button"
        onClick={onNavigate}
        className="mt-6 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-medium text-white transition-transform sm:hover:scale-[1.02] active:scale-[0.98] min-h-[44px] w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-black/20"
        style={{
          backgroundColor: "var(--vlp-primary)",
          boxShadow: "0 4px 14px 0 rgba(0,0,0,0.10)",
        }}
        data-testid={buttonTestid}
      >
        <span className="text-center leading-tight">{buttonText}</span>
        <span aria-hidden className="ml-2 shrink-0 transition-transform sm:group-hover:translate-x-0.5">→</span>
      </button>
    </article>
  );
}


function Footer({ v }) {
  const c = v.contact || {};
  const anyContact = c.phone || c.email || c.website || c.address_lines?.length;
  const year = new Date().getFullYear();
  return (
    <footer
      className="w-full text-white"
      style={{ backgroundColor: "var(--vlp-primary)" }}
      data-testid="vlp-footer"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <h3 className="text-lg font-semibold">{v.display_name}</h3>
          {v.subtitle && <p className="mt-2 text-sm text-white/80">{v.subtitle}</p>}
        </div>
        {anyContact && (
          <div className="text-sm text-white/90 space-y-1">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-2">Contact</h4>
            {c.address_lines?.length > 0 && (
              <p>{c.address_lines.join(", ")}</p>
            )}
            {c.phone && (
              <p><a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a></p>
            )}
            {c.email && (
              <p><a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a></p>
            )}
            {c.website && (
              <p><a href={c.website} target="_blank" rel="noreferrer" className="hover:underline break-all">{c.website}</a></p>
            )}
          </div>
        )}
        <div className="text-sm text-white/90">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-2">Explore</h4>
          <Link to={`/venue/${v.venue_id}/contact`} className="hover:underline">Contact and hours</Link>
        </div>
      </div>
      <div className="border-t border-white/15">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 text-xs text-white/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span>© {year} {v.display_name}. All rights reserved.</span>
          {v.footer_note && <span className="italic">{v.footer_note}</span>}
        </div>
      </div>
    </footer>
  );
}


export function ContactSummary({ contact }) {
  if (!contact) return null;
  const any = contact.phone || contact.email || contact.website || contact.address;
  if (!any) return null;
  return (
    <section
      className="rounded-2xl bg-white p-6 shadow-sm border border-stone-200/60"
      data-testid="vlp-contact-summary"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500">Contact</h3>
      <dl className="mt-3 space-y-2 text-sm text-stone-700">
        {contact.address_lines?.length > 0 && (
          <div className="flex gap-3" data-testid="vlp-address">
            <dt className="w-20 text-stone-500 shrink-0">Address</dt>
            <dd>{contact.address_lines.join(", ")}</dd>
          </div>
        )}
        {contact.phone && (
          <div className="flex gap-3" data-testid="vlp-phone">
            <dt className="w-20 text-stone-500 shrink-0">Phone</dt>
            <dd><a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a></dd>
          </div>
        )}
        {contact.email && (
          <div className="flex gap-3" data-testid="vlp-email">
            <dt className="w-20 text-stone-500 shrink-0">Email</dt>
            <dd className="min-w-0 break-all"><a href={`mailto:${contact.email}`} className="hover:underline">{contact.email}</a></dd>
          </div>
        )}
        {contact.website && (
          <div className="flex gap-3" data-testid="vlp-website">
            <dt className="w-20 text-stone-500 shrink-0">Website</dt>
            <dd className="min-w-0 break-all">
              <a href={contact.website} target="_blank" rel="noreferrer" className="underline">
                {contact.website}
              </a>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}


export function HoursSummary({ hours }) {
  if (!hours || hours.length === 0) return null;
  return (
    <section
      className="rounded-2xl bg-white p-6 shadow-sm border border-stone-200/60"
      data-testid="vlp-hours"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500">Business hours</h3>
      <dl className="mt-3 divide-y divide-stone-100 text-sm text-stone-700">
        {hours.map((h) => {
          const periods = Array.isArray(h.periods) && h.periods.length > 0
            ? h.periods
            : (h.closed || !h.open || !h.close
                ? []
                : [{ start_time: h.open, end_time: h.close,
                       display: h.display }]);
          return (
            <div
              key={h.day}
              className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-4 py-2 first:pt-0 last:pb-0"
              data-testid={`vlp-hours-${h.day.toLowerCase()}`}
            >
              <dt className={h.closed ? "text-stone-400" : "text-stone-600 font-medium"}>
                {h.day}
              </dt>
              <dd className={"sm:text-right " + (h.closed ? "text-stone-400 italic" : "")}>
                {h.closed && "Closed"}
                {!h.closed && periods.length === 0 && "—"}
                {!h.closed && periods.length > 0 && (
                  <ul className="space-y-0.5">
                    {periods.map((p, idx) => (
                      <li key={idx} className="font-medium tabular-nums whitespace-nowrap"
                          data-testid={`vlp-hours-${h.day.toLowerCase()}-period-${idx}`}>
                        {p.display}
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}


export function DeliverySummary({ delivery }) {
  // When delivery is disabled at the venue level we render nothing.
  // (The delivery card is a status surface, not a switch.)
  if (!delivery?.enabled) return null;

  const enabledDays = (delivery.days || []).filter((d) => d.enabled);
  const hasHours = delivery.hours_configured;

  return (
    <section
      className="rounded-2xl bg-white p-6 shadow-sm border border-stone-200/60"
      data-testid="vlp-delivery"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500">
        Delivery
      </h3>

      {delivery.area_note && (
        <p className="mt-3 text-sm text-stone-700" data-testid="vlp-delivery-area">
          {delivery.area_note}
        </p>
      )}
      {delivery.fee_note && (
        <p className="mt-1 text-sm text-stone-600" data-testid="vlp-delivery-fee">
          {delivery.fee_note}
        </p>
      )}

      {hasHours ? (
        <dl
          className="mt-3 space-y-1.5 text-sm text-stone-700"
          data-testid="vlp-delivery-hours"
        >
          {enabledDays.map((d) => (
            <div
              key={d.day}
              className="flex justify-between gap-3 border-b border-stone-100 last:border-0 pb-1.5 last:pb-0"
              data-testid={`vlp-delivery-day-${d.day.toLowerCase()}`}
            >
              <dt className="text-stone-600 shrink-0">{d.day}</dt>
              <dd className="text-right">{d.display || "—"}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p
          className="mt-4 text-sm text-stone-600 italic"
          data-testid="vlp-delivery-no-hours"
        >
          Delivery hours vary. Please contact the venue.
        </p>
      )}
    </section>
  );
}
